import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useSelector } from 'react-redux';

import { getLinksToCheck, getLinksToCheckCounts } from '../../api/links_to_check';
import { getMatchesByUserToday, releaseUrlData, reserveUrlData, updateUrlData } from '../../api/url_data';
import { getSmartImage } from '../../helpers/helpers';
import { createAnswerWindow, getAnswerFailure, getAnswerLabel, getAnswerUpdate, getCountsWithoutRecentAnswers, getTodayEntries } from '../../helpers/links_to_check_flow_helpers';
import { LINKS_TO_CHECK_TABS } from '../../helpers/links_to_check_helpers';
import {
  getMergeBlockedReason,
  getUndoneNotice,
  isLikelyWrongAnswer,
  isMergeAnswer,
  isUndoFirstAnswer,
  saveLikelyWrongAnswer,
  toUndoneLinkCard
} from '../../helpers/links_to_check_likely_wrong_helpers';
import { useInterval } from '../../hooks/useInterval';
import { useSearchParamsState } from '../../hooks/useSearchParamsState';

const PAGE_SIZE = 20;
const FETCH_MORE_BELOW = 5;
const UP_NEXT_COUNT = 3;
const COUNTS_REFRESH_MS = 30 * 1000;
// The counts route answers from a shared cache this old, so a fresh count can miss this page's latest answers.
const COUNTS_CACHE_MS = 30 * 1000;
const CLAIM_RENEW_MS = 60 * 1000;
// A card change sooner than this after the last is a double click, never a decision about the new card.
const CARD_SETTLE_MS = 400;
const QUESTIONS = LINKS_TO_CHECK_TABS.map(tab => tab.question);
const URL_DEFAULTS = { tab: QUESTIONS[0], site: '' };
const NOT_NOW_STORAGE_KEY = 'linksToCheckNotNow';
const ALREADY_DONE = 'A teammate already did this one.';
const CLAIM_NOTICES = {
  opening: { reserved: 'Someone else just opened that one.', resolved: ALREADY_DONE },
  renewing: { reserved: 'Someone else is on this link now. Here’s the next one.', resolved: ALREADY_DONE }
};

const LinksToCheckContext = createContext(null);

/**
 * Holds one question's links in the server's order, the claim on the link on screen, the tab
 * counts, Not now and the answers. Claims follow URL Data's rules: renewed every minute, released
 * on leave, and a link someone else holds or already answered is skipped.
 *
 * answer({ kind, ...args }) answers the link on screen:
 *   link: { product, metadata? }, skip: { reason }, help: { note }; sendNow: true skips the undo window.
 *   On Likely wrong cards: alert_right, alert_repoint: { product }, merge, keep_both, and undo_link: { reason },
 *   undo_add, undo_skip: { reason? }, which undo our link at once before a final answer can be saved.
 */
export const LinksToCheckProvider = ({ children }) => {
  const [urlState, patchUrl] = useSearchParamsState(URL_DEFAULTS);
  const currentUser = useSelector(state => state.users.currentUser);
  const question = QUESTIONS.includes(urlState.tab) ? urlState.tab : URL_DEFAULTS.tab;
  const { site } = urlState;
  const queueKey = `${question}|${site}`;

  const [queue, setQueue] = useState(() => getEmptyQueue(queueKey));
  const [counts, setCounts] = useState(null);
  const [claimState, setClaimState] = useState(null);
  const [passedKeys, setPassedKeys] = useState(readPassedKeys);
  const [paneState, setPaneState] = useState(null);
  const [sheetState, setSheetState] = useState(null);
  const [pending, setPending] = useState(null);
  const [failure, setFailure] = useState(null);
  const [history, setHistory] = useState([]);
  const [sessionEntries, setSessionEntries] = useState([]);
  const [isKeysOpen, setIsKeysOpen] = useState(false);
  const [undoingLinkId, setUndoingLinkId] = useState(null);

  // Queue
  const isQueueCurrent = queue.key === queueKey;
  const visibleLinks = isQueueCurrent ? queue.links.filter(item => !passedKeys.has(getPassKey(item))) : [];
  const link = visibleLinks[0] || null;
  const upNext = visibleLinks.slice(1, 1 + UP_NEXT_COUNT);
  const status = getQueueStatus({ queue: isQueueCurrent ? queue : null, link, counts, question, site });
  const needsMoreLinks = isQueueCurrent && queue.hasMore && !queue.isLoading && !queue.failed && visibleLinks.length < FETCH_MORE_BELOW;
  const claimStem = link?.cardType === 'decide' ? link.hashedUrlStem : null;
  const claim = getClaim(claimState, claimStem);
  const pane = paneState && paneState.linkId === link?.id ? paneState.name : null;
  const sheet = sheetState && sheetState.linkId === link?.id ? sheetState.name : null;
  const mergeBlockedReason = link ? getMergeBlockedReason(link, currentUser?.scopes || []) : null;
  const isUndoing = Boolean(link) && link.id === undoingLinkId;
  const todayEntries = getTodayEntries(history, sessionEntries);

  // Async work reads these so a reply never acts on a tab, link or claim that has moved on
  const offsetRef = useRef(0);
  const snapshotAtRef = useRef(null);
  const fetchingKeyRef = useRef(null);
  const doneIdsRef = useRef(new Set());
  const heldStemRef = useRef(null);
  const keptStemsRef = useRef(new Set());
  const recentAnswersRef = useRef([]);
  const shiftedSinceRef = useRef(0);
  const lastCardChangeAtRef = useRef(0);
  const mountedRef = useRef(true);
  const queueKeyRef = useRef(queueKey);
  const queueLinksRef = useRef(queue.links);
  const statusRef = useRef(status);
  const claimStemRef = useRef(claimStem);
  const claimStateRef = useRef(claimState);
  const linkIdRef = useRef(link?.id);
  const pendingRef = useRef(pending);
  queueKeyRef.current = queueKey;
  queueLinksRef.current = queue.links;
  statusRef.current = status;
  claimStemRef.current = claimStem;
  claimStateRef.current = claimState;
  linkIdRef.current = link?.id;
  pendingRef.current = pending;

  const loadLinks = async ({ fromStart = false } = {}) => {
    const key = queueKey;
    if (fetchingKeyRef.current === key) return;
    fetchingKeyRef.current = key;
    const offset = fromStart ? 0 : offsetRef.current;
    const shiftedAtStart = shiftedSinceRef.current;
    setQueue(current => (current.key === key ? { ...current, isLoading: true, failed: false } : current));
    try {
      const { links = [], hasMore = false, nextOffset, snapshotAt } = await getLinksToCheck({ question, domain: site || undefined, limit: PAGE_SIZE, offset,
        ...(snapshotAtRef.current ? { snapshotAt: snapshotAtRef.current } : {}) });
      if (queueKeyRef.current !== key) return;
      offsetRef.current = snapshotAt ? nextOffset : nextOffset - (shiftedSinceRef.current - shiftedAtStart);
      snapshotAtRef.current = snapshotAt ?? null;
      setQueue(current =>
        current.key !== key
          ? current
          : {
              ...current,
              links: _.uniqBy([...current.links, ...links], 'id').filter(item => !doneIdsRef.current.has(item.id)),
              hasMore: hasMore && (links.length > 0 || nextOffset > offset),
              isLoading: false,
              pageCount: current.pageCount + 1
            }
      );
    } catch (error) {
      if (queueKeyRef.current !== key) return;
      console.warn(`Links to check unavailable: ${error || 'unknown error'}`);
      setQueue(current => (current.key === key ? { ...current, isLoading: false, failed: true } : current));
    } finally {
      if (fetchingKeyRef.current === key) fetchingKeyRef.current = null;
    }
  };

  const reload = () => setQueue(current => ({ ...current, failed: false }));

  // A link that leaves the server's order (someone else holds it, or it is done) moves every later
  // link up one, so the next page starts one link earlier.
  const removeLink = (target, { isDone }) => {
    if (isDone) doneIdsRef.current.add(target.id);
    if (queueLinksRef.current.some(item => item.id === target.id)) shiftOffset(-1);
    setQueue(current => ({ ...current, links: current.links.filter(item => item.id !== target.id) }));
    setFailure(current => (current?.answer.link.id === target.id ? null : current));
    if (isDone) setCounts(current => adjustCount(current, target.question, -1));
  };

  // Undo puts the link back in front; a failed save puts it after the card on screen, so it never
  // swaps the card someone is in the middle of. Returns whether the link was this tab's.
  const restoreLink = (held, { inFront }) => {
    doneIdsRef.current.delete(held.link.id);
    setCounts(current => adjustCount(current, held.link.question, 1));
    if (held.queueKey !== queueKeyRef.current) return false;
    shiftOffset(1);
    setQueue(current => ({ ...current, links: placeLink(current.links, held.link, inFront ? null : linkIdRef.current) }));
    return true;
  };

  // A feed paged from a fixed snapshot keeps its order as links leave it, so only the live queue shifts.
  const shiftOffset = delta => {
    if (snapshotAtRef.current) return;
    offsetRef.current = Math.max(0, offsetRef.current + delta);
    shiftedSinceRef.current -= delta;
  };

  // Counts
  const loadCounts = () =>
    getLinksToCheckCounts()
      .then(resp => {
        recentAnswersRef.current = recentAnswersRef.current.filter(answer => !answer.sentAt || Date.now() - answer.sentAt < COUNTS_CACHE_MS);
        setCounts(getCountsWithoutRecentAnswers(resp.counts || {}, recentAnswersRef.current, { cacheMs: COUNTS_CACHE_MS }));
      })
      .catch(error => {
        setCounts(current => current || {});
        console.warn(`Links to check counts unavailable: ${error || 'unknown error'}`);
      });

  // Claims
  const releaseHeldClaim = () => {
    if (!heldStemRef.current) return;
    releaseClaim(heldStemRef.current);
    heldStemRef.current = null;
  };

  const claimLink = async (stem, { isRenewal }) => {
    try {
      const { data } = await reserveUrlData(stem);
      if (claimStemRef.current !== stem) return settleOffscreenClaim(stem, data);
      if (data.success) {
        heldStemRef.current = stem;
        setClaimState({ stem, state: 'yours', expiresAt: Date.now() + data.ttl * 1000 });
        return;
      }
      if (heldStemRef.current === stem) heldStemRef.current = null;
      const isResolved = data.reason !== 'reserved';
      setClaimState({ stem, state: 'lost', expiresAt: null });
      window.ALERT.info(CLAIM_NOTICES[isRenewal ? 'renewing' : 'opening'][isResolved ? 'resolved' : 'reserved']);
      const target = queueLinksRef.current.find(item => item.hashedUrlStem === stem);
      if (target) removeLink(target, { isDone: isResolved });
    } catch (error) {
      if (claimStemRef.current !== stem) return;
      // A failed renewal leaves the claim as it was; the next minute's renewal tries again.
      if (isRenewal) return console.warn(`Claim renewal failed: ${error || 'unknown error'}`);
      setClaimState({ stem, state: 'lost', expiresAt: null });
      window.ALERT.error('Couldn’t open this link. Trying again in a minute.');
    }
  };

  const claimLinkRef = useRef(claimLink);
  claimLinkRef.current = claimLink;

  // Queue effects
  useEffect(() => {
    offsetRef.current = 0;
    snapshotAtRef.current = null;
    setQueue(current => (current.key === queueKey ? current : getEmptyQueue(queueKey)));
  }, [queueKey]);

  useEffect(() => {
    if (needsMoreLinks) loadLinks();
  }, [needsMoreLinks, queue.pageCount]);

  // Count effects
  useEffect(() => {
    loadCounts();
  }, []);

  useInterval(() => {
    if (document.visibilityState === 'hidden') return;
    loadCounts();
    if (statusRef.current === 'with_teammates') loadLinks({ fromStart: true });
  }, COUNTS_REFRESH_MS);

  // Claim effects. A claim is released as soon as its link leaves the screen: Not now, another tab
  // or site, or leaving the page.
  useEffect(() => {
    if (!claimStem) return;
    claimLinkRef.current(claimStem, { isRenewal: false });
    return () => heldStemRef.current === claimStem && releaseHeldClaim();
  }, [claimStem]);

  useInterval(() => claimLinkRef.current(claimStemRef.current, { isRenewal: true }), claimStem ? CLAIM_RENEW_MS : null);

  // After a laptop sleeps, a claim close to running out is checked again before anything else.
  useEffect(() => {
    const handleVisibilityChange = () => {
      const stem = claimStemRef.current;
      if (document.visibilityState !== 'visible' || !stem) return;
      const expiresAt = claimStateRef.current?.stem === stem ? claimStateRef.current.expiresAt : null;
      if (expiresAt && expiresAt - Date.now() > CLAIM_RENEW_MS) return;
      if (expiresAt && expiresAt < Date.now()) setClaimState({ stem, state: 'claiming', expiresAt: null });
      claimLinkRef.current(stem, { isRenewal: true });
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Answers. The link leaves the queue at once while its save waits out the undo window; the
  // claim stays with the answer until the save lands.
  const forgetAnswer = held => {
    recentAnswersRef.current = recentAnswersRef.current.filter(answer => answer.id !== held.id);
  };

  const sendAnswer = async held => {
    const stem = held.link.hashedUrlStem;
    try {
      const isLikelyWrong = isLikelyWrongAnswer(held.kind);
      const saved = isLikelyWrong
        ? await saveLikelyWrongAnswer(held.link, { kind: held.kind, ...held.args })
        : await updateUrlData(stem, getAnswerUpdate({ kind: held.kind, ...held.args }, held.decidedAt));
      keptStemsRef.current.delete(stem);
      // Only the URL Data save claims the link; the Likely wrong routes never do.
      if (!isLikelyWrong) releaseClaim(stem);
      recentAnswersRef.current = recentAnswersRef.current.map(answer => (answer.id === held.id ? { ...answer, sentAt: Date.now() } : answer));
      if (!mountedRef.current) return;
      if (saved?.alreadyDone) window.ALERT.info(ALREADY_DONE);
      else setSessionEntries(entries => [{ key: held.id, label: held.label, at: new Date().toISOString(), hashedUrlStem: stem }, ...entries]);
    } catch (error) {
      keptStemsRef.current.delete(stem);
      forgetAnswer(held);
      const message = _.compact([getAnswerFailure(held), typeof error === 'string' && error]).join(' ');
      if (!mountedRef.current) return window.ALERT.error(message);
      if (!restoreLink(held, { inFront: false })) releaseClaim(stem);
      setFailure({ answer: held, message });
    }
  };

  const sendAnswerRef = useRef(sendAnswer);
  sendAnswerRef.current = sendAnswer;
  const answerWindowRef = useRef(null);
  if (!answerWindowRef.current) answerWindowRef.current = createAnswerWindow({ onSend: held => sendAnswerRef.current(held), onChange: setPending });

  const submitAnswer = (target, { kind, sendNow = false, ...args }, answerQueueKey = queueKey) => {
    const held = { id: _.uniqueId('answer-'), kind, args, link: target, queueKey: answerQueueKey, decidedAt: Date.now(), label: getAnswerLabel({ kind, ...args }, target) };
    keptStemsRef.current.add(target.hashedUrlStem);
    recentAnswersRef.current = [...recentAnswersRef.current, { id: held.id, question: target.question, sentAt: null }];
    if (heldStemRef.current === target.hashedUrlStem) heldStemRef.current = null;
    removeLink(target, { isDone: true });
    setPaneState(null);
    setSheetState(null);
    setFailure(null);
    if (!sendNow) return answerWindowRef.current.hold(held);
    answerWindowRef.current.flush();
    sendAnswer(held);
  };

  // A product picked on an alert card goes through the correction route: a plain link would leave
  // what our link spread to in place.
  const answer = args => {
    if (!link || isUndoing || !settleCardChange()) return;
    const resolved = args.kind === 'link' && link.cardType === 'alert'
      ? { ...args, kind: args.product.id === link.applied.product.id ? 'alert_right' : 'alert_repoint' }
      : args;
    if (isMergeAnswer(link, resolved) && mergeBlockedReason) return window.ALERT.info(mergeBlockedReason);
    if (isUndoFirstAnswer(resolved)) return undoOurLink(link, resolved);
    submitAnswer(link, resolved);
  };

  // Undoing our link is sent at once, so the alert can turn into the link's ordinary card. The answer that finishes it
  // waits out the undo window, except an add's skip: U would bring back a card that no longer shows the product we added.
  const undoOurLink = async (target, args) => {
    const key = queueKey;
    const skipReason = args.kind === 'undo_skip' ? args.reason : null;
    setUndoingLinkId(target.id);
    answerWindowRef.current.flush();
    try {
      const saved = await saveLikelyWrongAnswer(target, args);
      if (saved?.alreadyDone) {
        removeLink(target, { isDone: true });
        window.ALERT.info(ALREADY_DONE);
        return;
      }
      if (skipReason) {
        const { data } = await reserveUrlData(target.hashedUrlStem);
        if (!data.success) {
          removeLink(target, { isDone: data.reason !== 'reserved' });
          window.ALERT.info(data.reason === 'reserved' ? CLAIM_NOTICES.opening.reserved : ALREADY_DONE);
          return;
        }
        if (!mountedRef.current || queueKeyRef.current !== key) {
          releaseClaim(target.hashedUrlStem);
          return;
        }
        heldStemRef.current = target.hashedUrlStem;
      }
    } catch (error) {
      window.ALERT.error(_.compact(['Couldn’t undo our link.', typeof error === 'string' && error]).join(' '));
      return;
    } finally {
      if (mountedRef.current) setUndoingLinkId(null);
    }
    if (!mountedRef.current) return;
    if (!skipReason) window.ALERT.info(getUndoneNotice(args.kind));
    recentAnswersRef.current = [...recentAnswersRef.current, { id: _.uniqueId('answer-'), question: target.question, sentAt: Date.now() }];
    setCounts(current => adjustCount(current, target.question, -1));
    if (queueKeyRef.current !== key) return;
    setQueue(current => ({ ...current, links: current.links.map(item => (item.id === target.id ? toUndoneLinkCard(item) : item)) }));
    if (skipReason) {
      submitAnswer(toUndoneLinkCard(target), { kind: 'skip', reason: skipReason, sendNow: target.applied.action === 'create' }, key);
      return;
    }
    setPaneState(args.kind === 'undo_add' ? { linkId: target.id, name: 'create' } : null);
    setSheetState(args.kind === 'undo_skip' ? { linkId: target.id, name: 'skip' } : null);
  };

  const retry = () => failure && submitAnswer(failure.answer.link, { kind: failure.answer.kind, ...failure.answer.args, sendNow: true }, failure.answer.queueKey);
  const clearFailure = () => setFailure(null);

  const undo = () => {
    const undone = answerWindowRef.current.undo();
    if (!undone) return;
    keptStemsRef.current.delete(undone.link.hashedUrlStem);
    forgetAnswer(undone);
    setPaneState(null);
    setSheetState(null);
    if (!restoreLink(undone, { inFront: true })) releaseClaim(undone.link.hashedUrlStem);
    window.ALERT.info('Undone. Nothing was saved.');
  };

  // A teammate finished a link while its answer here still waited to be sent: the answer is
  // dropped, so it can't overwrite theirs.
  const settleOffscreenClaim = (stem, data) => {
    if (data?.success) return keptStemsRef.current.has(stem) || releaseClaim(stem);
    if (data?.reason === 'reserved' || pendingRef.current?.link.hashedUrlStem !== stem) return;
    const dropped = answerWindowRef.current.undo();
    keptStemsRef.current.delete(stem);
    forgetAnswer(dropped);
    window.ALERT.info(CLAIM_NOTICES.opening.resolved);
  };

  const settleCardChange = () => {
    if (Date.now() - lastCardChangeAtRef.current < CARD_SETTLE_MS) return false;
    lastCardChangeAtRef.current = Date.now();
    return true;
  };

  // Hiding or leaving the page sends the answer still waiting.
  useEffect(() => {
    mountedRef.current = true;
    const sendWhenHidden = () => document.visibilityState === 'hidden' && answerWindowRef.current.flush();
    document.addEventListener('visibilitychange', sendWhenHidden);
    return () => {
      document.removeEventListener('visibilitychange', sendWhenHidden);
      mountedRef.current = false;
      answerWindowRef.current.flush();
    };
  }, []);

  useEffect(() => {
    getMatchesByUserToday()
      .then(resp => mountedRef.current && setHistory(resp.data || []))
      .catch(error => console.warn(`Today's matches unavailable: ${error || 'unknown error'}`));
  }, []);

  // Not now
  const notNow = () => {
    if (link && settleCardChange()) setPassedKeys(current => new Set(current).add(getPassKey(link)));
  };

  useEffect(() => {
    writePassedKeys(passedKeys);
  }, [passedKeys]);

  // The next cards' photos load while this one is on screen
  const upNextIds = upNext.map(item => item.id).join(',');
  useEffect(() => {
    _.uniq(upNext.flatMap(getLinkPhotos)).forEach(prefetchPhoto);
  }, [upNextIds]);

  // Panes and sheets belong to the link they were opened on
  const openPane = name => {
    setSheetState(null);
    setPaneState({ linkId: link?.id, name });
  };
  const closePane = () => setPaneState(null);
  const openSheet = name => setSheetState({ linkId: link?.id, name });
  const closeSheet = () => setSheetState(null);

  const toggleKeys = () => setIsKeysOpen(open => !open);
  const closeKeys = () => setIsKeysOpen(false);

  const setQuestion = next => {
    setPaneState(null);
    setSheetState(null);
    patchUrl({ tab: next, site: '' });
  };
  const setSite = next => {
    setPaneState(null);
    setSheetState(null);
    patchUrl({ site: next || '' });
  };

  return (
    <LinksToCheckContext.Provider
      value={{
        question,
        setQuestion,
        counts: counts || {},
        site,
        setSite,
        link,
        upNext,
        status,
        claim,
        pane,
        openPane,
        closePane,
        sheet,
        openSheet,
        closeSheet,
        mergeBlockedReason,
        isUndoing,
        answer,
        notNow,
        undo,
        pending,
        today: { count: todayEntries.length, entries: todayEntries },
        failure,
        retry,
        clearFailure,
        isKeysOpen,
        toggleKeys,
        closeKeys,
        reload
      }}
    >
      {children}
    </LinksToCheckContext.Provider>
  );
};

LinksToCheckProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export const useLinksToCheckContext = () => {
  const context = useContext(LinksToCheckContext);
  if (!context) throw new Error('useLinksToCheckContext must be used within a LinksToCheckProvider');
  return context;
};

// Until its own reply arrives, a newly shown link reads as being claimed, never as the last one's claim.
const getClaim = (claimState, stem) => {
  if (!stem) return null;
  if (claimState?.stem !== stem) return { state: 'claiming', expiresAt: null };
  return { state: claimState.state, expiresAt: claimState.expiresAt };
};

const getEmptyQueue = key => ({ key, links: [], hasMore: true, isLoading: false, failed: false, pageCount: 0 });

const getPassKey = link => `${link.question}:${link.id}`;

// "With teammates" means the counts still see more links than this page could load; counts cover
// every site, so a site filter that runs dry is simply empty.
const getQueueStatus = ({ queue, link, counts, question, site }) => {
  if (link) return 'ready';
  if (queue?.failed) return 'error';
  if (!queue || queue.hasMore || queue.isLoading || !counts) return 'loading';
  return !site && (counts[question]?.waiting || 0) > queue.links.length ? 'with_teammates' : 'empty';
};

const placeLink = (links, target, afterId) => {
  const rest = links.filter(item => item.id !== target.id);
  const afterIndex = afterId == null ? -1 : rest.findIndex(item => item.id === afterId);
  return [...rest.slice(0, afterIndex + 1), target, ...rest.slice(afterIndex + 1)];
};

const adjustCount = (counts, question, delta) => {
  if (!counts?.[question]) return counts;
  return { ...counts, [question]: { ...counts[question], waiting: Math.max(0, counts[question].waiting + delta) } };
};

// A release that fails lapses on its own when the claim's ten minutes run out.
const releaseClaim = stem => releaseUrlData(stem).catch(() => {});

const readPassedKeys = () => {
  try {
    return new Set(JSON.parse(window.sessionStorage.getItem(NOT_NOW_STORAGE_KEY)) || []);
  } catch {
    return new Set();
  }
};

const writePassedKeys = keys => {
  try {
    window.sessionStorage.setItem(NOT_NOW_STORAGE_KEY, JSON.stringify([...keys]));
  } catch {
    // Without storage, Not now still holds until the page reloads.
  }
};

const getLinkPhotos = link =>
  [
    link.page?.image,
    link.creatorPin?.image,
    link.pick?.image,
    ...(link.candidates || []).map(product => product.image),
    link.proposal?.image,
    ...(link.similarInCatalog || []).map(product => product.image),
    link.applied?.product?.image,
    link.pair?.keep?.image,
    link.pair?.remove?.image
  ].filter(source => source && source !== 'null');

// Same request the card's SafeImage makes, so the next card's photos come out of the browser cache.
const prefetchPhoto = source => {
  const image = new Image();
  image.referrerPolicy = 'no-referrer';
  image.src = getSmartImage(source);
};
